import { classInstance } from '@/shared/helpers/helpers';
import { VideoCursor } from './video-cursor';

/**
 * @desc UI Компонент Video
 * @category 2 Common
 * @example
 * document.querySelectorAll('.js-video').forEach((element) => {new Video(element)});
 * @constructor
 * @param {HTMLElement} selector - HTMLElement контейнера
 * @param {Object} [options] - опции конфигурации
 * @param {String} [options.selectorButton=".js-video__play"] - селектор элемента воспроизведения
 */

export class Video {
	readonly $container: HTMLElement;
	readonly selectorButton: string;
	readonly selectorSoundButton: string;

	protected $button: HTMLElement;
	protected $video: HTMLVideoElement;
	protected $soundButton: HTMLElement;
	private videoCursor: VideoCursor | null = null;
	private observer: IntersectionObserver | null = null;
	private captionTimeout: number | null = null;

	private readonly classActive: string = 'video--active';
	private readonly classSoundMuted: string = 'video__sound-button--muted';
	private readonly classCaption: string = '.js-video-caption';
	private readonly classCaptionHidden: string = 'hero-video__caption--hidden';

	private clickHandler: () => void;
	private endedHandler: () => void;
	private soundHandler: () => void;
	private volumeHandler: () => void;
	private cursorActionHandler: (e: CustomEvent) => void;

	private $heroVideoContainer: HTMLElement | null = null;

	constructor(selector: HTMLElement, options: { selectorButton?: string; selectorSoundButton?: string } = {}) {
		this.$container = selector;
		if (!this.$container) return;

		this.selectorButton = options.selectorButton || '.js-video__play';
		this.selectorSoundButton = options.selectorSoundButton || '.js-video__sound-button';

		this.init();
	}

	/**
	 * @desc Инициализировать компонент
	 */
	public init(): void {
		classInstance.set(this.$container, { video: this });

		this.$video = this.$container.querySelector('video');
		this.$button = this.$container.querySelector(this.selectorButton);
		this.$soundButton = this.$container.querySelector(this.selectorSoundButton);

		this.clickHandler = this.clickHandlerFn.bind(this);
		this.endedHandler = this.endedHandlerFn.bind(this);
		this.soundHandler = this.soundHandlerFn.bind(this);
		this.volumeHandler = this.volumeHandlerFn.bind(this);

		this.$button?.addEventListener('click', this.clickHandler);
		this.$video?.addEventListener('ended', this.endedHandler);
		this.$soundButton?.addEventListener('click', this.soundHandler);
		this.$video?.addEventListener('volumechange', this.volumeHandler);

		this.updateSoundButton();

		if (this.$container.classList.contains('js-video--cursor')) {
			this.videoCursor = new VideoCursor(this.$container);

			this.cursorActionHandler = this.cursorActionHandlerFn.bind(this);
			this.$container.addEventListener('video-cursor-action', this.cursorActionHandler);
		}

		if (this.$container.classList.contains('js-video--observer')) {
			this.initObserver();
		}

		this.$heroVideoContainer = this.$container.closest('.js-hero-video');
	}

	/**
	 * @desc Инициализация IntersectionObserver
	 */
	private initObserver(): void {
		if (!this.$video) return;

		this.observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						this.play();

						if (this.$heroVideoContainer) {
							this.showHeroCaptions();
							this.startHeroCaptionTimer();
						}
					} else {
						this.pause();

						if (this.$heroVideoContainer) {
							this.resetHeroCaptions();
						}
					}
				});
			},
			{
				root: null,
				threshold: 0.5,
			},
		);

		this.observer.observe(this.$container);
	}

	private showHeroCaptions(): void {
		if (!this.$heroVideoContainer) return;
		this.$heroVideoContainer.querySelectorAll(this.classCaption).forEach((caption) => {
			caption.classList.remove(this.classCaptionHidden);
		});
	}

	private startHeroCaptionTimer(): void {
		this.clearHeroCaptionTimer();
		this.captionTimeout = window.setTimeout(() => {
			if (!this.$heroVideoContainer) return;
			this.$heroVideoContainer.querySelectorAll(this.classCaption).forEach((caption) => {
				caption.classList.add(this.classCaptionHidden);
			});
		}, 10000);
	}

	private resetHeroCaptions(): void {
		this.clearHeroCaptionTimer();
		if (!this.$heroVideoContainer) return;
		this.$heroVideoContainer.querySelectorAll(this.classCaption).forEach((caption) => {
			caption.classList.remove(this.classCaptionHidden);
		});
	}

	private clearHeroCaptionTimer(): void {
		if (this.captionTimeout) {
			clearTimeout(this.captionTimeout);
			this.captionTimeout = null;
		}
	}

	/**
	 * @desc Удалить обработчики событий
	 */
	public destroy(): void {
		classInstance.del(this.$container, 'video');

		this.$button?.removeEventListener('click', this.clickHandler);
		this.$video?.removeEventListener('ended', this.endedHandler);
		this.$soundButton?.removeEventListener('click', this.soundHandler);
		this.$video?.removeEventListener('volumechange', this.volumeHandler);

		if (this.videoCursor) {
			this.$container.removeEventListener('video-cursor-action', this.cursorActionHandler);
			this.videoCursor.destroy();
			this.videoCursor = null;
		}

		if (this.observer) {
			this.observer.disconnect();
			this.observer = null;
		}

		this.clearHeroCaptionTimer();
	}

	/**
	 * @desc Переопределить обрабочики событий
	 * @example
	 * const myVideo = app.classInstance.get(document.querySelector('.js-video'));
	 * myTabs.video.reinit();
	 */
	public reinit(): void {
		this.destroy();
		this.init();
	}

	/**
	 * @desc Воспроизвести видео
	 * @example
	 * const myVideo = app.classInstance.get(document.querySelector('.js-video'));
	 * myTabs.video.play();
	 */
	public play(): void {
		this.$container.classList.add(this.classActive);

		if (!this.$container.classList.contains('js-video--observer') && !this.$container.classList.contains('js-video--no-scroll')) {
			this.scrollTo();
		}

		this.$video.play();
	}

	/**
	 * @desc Остановить воспроизведение видео
	 * @example
	 * const myVideo = app.classInstance.get(document.querySelector('.js-video'));
	 * myTabs.video.pause();
	 */
	public pause(): void {
		this.$video.pause();
		this.$container.classList.remove(this.classActive);
	}

	public toggleSound(): void {
		if (!this.$video) return;

		if (this.$video.muted || this.$video.volume === 0) {
			this.$video.muted = false;
			if (this.$video.volume === 0) this.$video.volume = 1.0;
		} else {
			this.$video.muted = true;
		}

		this.updateSoundButton();
	}

	private updateSoundButton(): void {
		if (!this.$video) return;

		const isSilent = this.$video.muted || this.$video.volume === 0;
		this.$soundButton?.classList.toggle(this.classSoundMuted, isSilent);
	}

	private clickHandlerFn(): void {
		this.play();
	}

	private endedHandlerFn(): void {
		this.$container.classList.remove(this.classActive);
	}

	private soundHandlerFn(): void {
		this.toggleSound();
	}

	private volumeHandlerFn(): void {
		this.updateSoundButton();
	}

	private cursorActionHandlerFn(e: CustomEvent): void {
		const action = e.detail;
		if (action === 'play') {
			this.$video.paused ? this.play() : this.pause();
			if (this.$video.paused) {
				this.$container.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
					inline: 'center',
				});
			}
		} else if (action === 'sound') {
			this.toggleSound();
		}
	}

	private scrollTo(customOffset = 20, smooth = true): void {
		const header = document.querySelector('header');
		const headerHeight = header ? header.scrollHeight : 0;

		const elementTop = this.$container.getBoundingClientRect().top;
		const scrollPosition = elementTop - headerHeight - customOffset;

		window.scrollTo({
			top: scrollPosition,
			behavior: smooth ? 'smooth' : 'auto',
		});
	}
}
